import React from "react";

/**
 * Verbatim CEO message from the HidroVerse Company Profile PDF (page 23).
 * Author: Dirk van Schalkwyk, CEO.
 *
 * Rendered as a wide charcoal banner — eyebrow + headline + signature on the
 * left, the message body on the right. No portrait (Dirk lives in the team
 * grid). Used as a closing personal moment at the bottom of /about and /team.
 */
export default function CEOMessage({ id = "ceo-message", showCTA = false }) {
  return (
    <section
      id={id}
      data-testid="ceo-message"
      className="px-6 md:px-12 lg:px-20 py-14 md:py-16"
      style={{ background: "#1F2227", color: "#F5F7FA" }}
    >
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        {/* LEFT — eyebrow, headline, signature */}
        <div className="lg:col-span-5">
          <div
            className="mb-5 text-[0.82rem] font-semibold"
            style={{ color: "rgba(245,247,250,0.55)" }}
          >
            A word from the CEO
          </div>

          <h2
            className="font-display"
            style={{
              fontSize: "clamp(2.2rem, 4.4vw, 3.8rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.025em",
              fontWeight: 300,
              color: "#F5F7FA",
            }}
          >
            Beyond <span style={{ color: "#1FB6C1", fontWeight: 600 }}>More.</span>
          </h2>

          <div
            className="mt-8 pt-6 border-t inline-block"
            style={{ borderColor: "rgba(31,182,193,0.30)" }}
          >
            <div
              className="font-display"
              style={{ fontSize: "1.25rem", letterSpacing: "-0.01em", color: "#F5F7FA", fontWeight: 500 }}
            >
              Dirk van Schalkwyk
            </div>
            <div
              className="mt-2 text-[0.78rem] font-semibold"
              style={{ color: "rgba(31,182,193,0.85)", letterSpacing: "0.04em" }}
            >
              CEO · HidroVerse
            </div>
          </div>

          {showCTA && (
            <div className="mt-8">
              <a
                href="/contact"
                className="inline-flex items-center gap-2 font-mono uppercase transition-colors duration-300 hover:opacity-80"
                style={{ fontSize: "0.66rem", letterSpacing: "0.24em", color: "#1FB6C1", fontWeight: 700 }}
                data-testid="ceo-cta"
              >
                Contact us <span aria-hidden>→</span>
              </a>
            </div>
          )}
        </div>

        {/* RIGHT — verbatim message body */}
        <div className="lg:col-span-7">
          <div
            className="space-y-4 font-light"
            style={{
              fontSize: "0.98rem",
              lineHeight: 1.7,
              color: "rgba(245,247,250,0.82)",
              maxWidth: "62ch",
            }}
          >
            <p>
              I have this saying: <em style={{ color: "#1FB6C1", fontStyle: "italic" }}>"Beyond More"</em>.
              It's more than a phrase to me; it's a discipline, a mindset, and the heartbeat
              behind how I choose to live and lead. It means showing up fully, looking for
              the next layer, and taking pride in every detail. It's not because perfection
              is the goal, but because excellence is an attitude. HidroVerse was born from
              that philosophy.
            </p>
            <p>
              What we have is a team driven by curiosity, excellence, and the desire to raise
              the bar every single time. We hold ourselves to the standards we'd expect if we
              were the client — from office staff to management to technicians.
            </p>
            <p>
              For our clients in the hotel, lodge, and hospitality industry: you receive more
              than a solution. You receive a team committed to delivering beyond the expected.
              Your guests deserve the best, and our promise is that we will always go beyond,
              to ensure that you can too.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
